import { useMemo, useState } from "react";
import styles from "./DealerAnalytics.module.scss";
import { Card, Empty, ReportFrame, Segmented, SortTable, StateBadge, fmtNum, fmtUsd, timeAgo, useReport } from "./shared";

const SORTS = [
  { value: "score", label: "Most interest" },
  { value: "views", label: "Views" },
  { value: "saves", label: "Saves" },
  { value: "leads", label: "Leads" },
  { value: "lastActivity", label: "Latest" },
];

const METRICS = [["views", "Views"], ["saves", "Saves"], ["leads", "Leads"], ["inspections", "Insp."], ["sales", "Sold"]];

const Listings = ({ range, refreshKey }) => {
  const state = useReport("listings", range, refreshKey);
  const [sort, setSort] = useState("score");
  const [hideGone, setHideGone] = useState(false);
  const [view, setView] = useState("cards");

  return (
    <ReportFrame state={state}>
      {(d) => (
        <ListingsBody listings={d.listings} sort={sort} setSort={setSort} hideGone={hideGone} setHideGone={setHideGone} view={view} setView={setView} />
      )}
    </ReportFrame>
  );
};

const ListingsBody = ({ listings, sort, setSort, hideGone, setHideGone, view, setView }) => {
  const rows = useMemo(() => {
    const list = hideGone ? listings.filter((l) => l.state !== "sold" && l.state !== "removed") : listings;
    const value = (l) => (sort === "lastActivity" ? new Date(l.lastActivity || 0).getTime() : l[sort]);
    return [...list].sort((a, b) => value(b) - value(a));
  }, [listings, sort, hideGone]);

  return (
    <Card
      title="Cars getting the most attention"
      sub="Interest weighs a view 1, a save 5, a lead 20, an inspection 30 and a sale 50. Photo, price and status are today's, from the listing."
      actions={(
        <div className={styles.filterRow}>
          <Segmented label="Sort" options={SORTS} value={sort} onChange={setSort} />
          <Segmented label="Layout" options={[{ value: "cards", label: "Cards" }, { value: "table", label: "Table" }]} value={view} onChange={setView} />
          <label className={styles.checkbox}>
            <input type="checkbox" checked={hideGone} onChange={(e) => setHideGone(e.target.checked)} />
            Hide sold and gone
          </label>
        </div>
      )}
    >
      {!rows.length ? <Empty>No listing activity in this period.</Empty> : view === "cards" ? (
        <div className={styles.listingGrid}>
          {rows.map((l) => (
            <article key={l.vehicleId} className={styles.listingCard}>
              {l.imageURL
                ? <img className={styles.listingImg} src={l.imageURL} alt="" loading="lazy" referrerPolicy="no-referrer" />
                : <div className={styles.listingImg} />}
              <div className={styles.listingBody}>
                <a className={styles.listingTitle} href={l.url} target="_blank" rel="noopener noreferrer">{l.title}</a>
                <div className={styles.listingMeta}>
                  <span>{l.usdPrice ? fmtUsd(l.usdPrice) : <span className={styles.muted}>ASK</span>}</span>
                  <StateBadge state={l.state} />
                </div>
                <span className={`${styles.small} ${styles.muted}`}>
                  {l.source === "current-inventory" ? "In the US · " : ""}last activity {timeAgo(l.lastActivity)}
                </span>
                <div className={styles.metricRow}>
                  {METRICS.map(([key, label]) => (
                    <div key={key}>
                      <strong>{fmtNum(l[key])}</strong>
                      <span>{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <SortTable
          rowKey={(r) => r.vehicleId}
          rows={rows}
          columns={[
            {
              key: "title",
              label: "Car",
              render: (r) => (
                <span className={styles.carCell}>
                  {r.imageURL ? <img className={styles.thumb} src={r.imageURL} alt="" loading="lazy" referrerPolicy="no-referrer" /> : <span className={styles.thumb} />}
                  <a className={styles.link} href={r.url} target="_blank" rel="noopener noreferrer">{r.title}</a>
                </span>
              ),
            },
            { key: "usdPrice", label: "Price", num: true, render: (r) => (r.usdPrice ? fmtUsd(r.usdPrice) : "ASK") },
            { key: "state", label: "Now", render: (r) => <StateBadge state={r.state} /> },
            { key: "views", label: "Views", num: true, bar: true },
            { key: "visitors", label: "Visitors", num: true },
            { key: "saves", label: "Saves", num: true },
            { key: "leads", label: "Leads", num: true },
            { key: "inspections", label: "Insp.", num: true },
            { key: "sales", label: "Sold", num: true },
            { key: "score", label: "Interest", num: true },
            { key: "lastActivity", label: "Last", num: true, sort: (r) => new Date(r.lastActivity || 0).getTime(), render: (r) => timeAgo(r.lastActivity) },
          ]}
        />
      )}
    </Card>
  );
};

export default Listings;
